import angular from 'angular';
import jss from 'jss';
import preset from 'jss-preset-default';
import jssPluginCamelCase from 'jss-plugin-camel-case';
import Logger from '../../shared/logger';

jss.setup(preset());
jss.use(jssPluginCamelCase);

/**
 * @function Async
 * Decorator for Components which inherits from BaseComponent.
 * Sync async code and angular digest.
 * It automatically will apply scope after function is return value.
 * You don't need to wrap async code into angular timeout callback.
 * @example
 * ```
 * class ExampleClass extends BaseComponent {
 *   @Inject('dataService') dataService;
 *
 *   onLoadDataButtonClick() {
 *      this.loadData();
 *   }
 *
 *   @Async()
 *   async loadData() {
 *      this.data = this.dataService.loadData();
 *   }
 * }
 * ```
 * */
export const Async = () => {
    return function decorator(target, n, descriptor) {
        const original = descriptor.value;

        if (typeof original === 'function') {
            descriptor.value = async function(...args) {
                let result;

                try {
                    typeof this.startLoading === 'function' && this.startLoading();
                    result = await original.apply(this, args);
                } catch (error) {
                    Logger.error(error.message);
                } finally {
                    typeof this.stopLoading === 'function' && this.stopLoading();
                    typeof this.applyScope  === 'function' && this.applyScope();
                }

                return result;
            }
        }

        return descriptor;
    };
};

/**
 * @function Inject
 * Decorator for Components which inherits from BaseComponent.
 * Allows to inject any injectable values to component.
 * It automatically will inject dependencies to component context.
 * You don't need to do it manually in constructor.
 * @example
 * ```
 * class ExampleClass extends BaseComponent {
 *   @Inject('notificationService') notificationService;
 *
 *   $onInit() {
 *      this.notificationService.success(this.data);
 *   }
 * }
 * ```
 * */
export const Inject = (injectableName) => {
    return function decorator(target, name, descriptor) {
        const isNgInjectExist = !!target.constructor.$inject;
        const isInjectablePropertiesExist = !!target.constructor.injectableProperties;

        if (isNgInjectExist) {
            target.constructor.$inject.push(injectableName);
        } else {
            target.constructor.$inject = [injectableName];
        }

        if (isInjectablePropertiesExist) {
            target.constructor.injectableProperties.push(name);
        } else {
            target.constructor.injectableProperties = [name];
        }

        descriptor.writable = true;
        descriptor.initializer = function () {
            return this[name];
        };

        return descriptor;
    };
};

/**
 * @function withAngularComponent
 * Decorator for Components which inherits from BaseComponent.
 * This decorator allows to register angular component
 * Features:
 * 1. Automatically will bind this class as component controller;
 * 2. Can skip the module, default is 'TALENYA-FE'
 * 3. By default add transclude option
 * 4. Register component with provided name.
 * 5. Automatically bind controllerAs options as name + 'Ctrl';
 * 6. Automatically add 'classes' property to binding option.
 * @example
 * ```
 * @withAngularComponent({
 *   name: 'example',
 *   bindings: {
 *      data: '<',
 *      onDataChange: '&',
 *   },
 * })
 * class ExampleClass extends BaseComponent {
 *   @Inject('notificationService') notificationService;
 *
 *   $onInit() {
 *      this.notificationService.success(this.data);
 *   }
 * }
 * ```
 * */
export const withAngularComponent = ({ name, module = 'TALENYA-FE', bindings, ...restOptions }) => (Component) => {
    angular.module(module)
        .component(name, {
            controller: Component,
            controllerAs: `${name}Ctrl`,
            transclude: true,
            bindings: {
                classes: '<',
                ...bindings,
            },
            ...restOptions,
        });

    return Component;
};

/**
 * @function withStyles
 * Decorator for Components which inherits from BaseComponent.
 * Should be used with @withAngularComponent
 * This decorator allows use jss styles
 * */
export const withStyles = (styles) => (Component) => {
    const sheet = jss.createStyleSheet(styles);

    sheet.attach();

    class WrappedComponent extends Component {
        constructor(...args) {
            super(...args);

            this.$scope.classes = sheet.classes;
        }

        $onChanges(...args) {
            super.$onChanges(...args);

            if (this.classes) {
                this.$scope.classes = {
                    ...this.$scope.classes,
                    ...this.classes,
                };
            }
        }
    }

    return WrappedComponent;
};